import { useSelector } from 'react-redux';

import { RootState } from '@app/redux/types';
import {
	checkEmailSetupCompleted,
	checkSMSSetupCompleted,
	checkWhatsAppSetupCompleted,
	checkWorkspaceHasReelsPlan,
	checkWorkspaceHasSMSPlan,
	checkWorkspaceHasVideoMessagingPlan,
} from '@app/utils/modules/workspace';

/**
 * Whether the workspace is "advance only" — an advance-track workspace that
 * should see the reduced experience without the legacy campaign/video building
 * chrome (nav items, settings sections and routes are hidden/disabled).
 *
 * A Niche Inbox workspace leaves the reduced experience once a sending channel
 * is set up together with the plan that sells it:
 * - text: SMS setup (SMS plan + Twilio subaccount), or WhatsApp setup on an
 *   SMS, video messaging or reels plan;
 * - email: video messaging or reels plan + SendGrid subuser.
 *
 * Returns false until the workspace payload has loaded, so routes gated on it
 * never redirect before the workspace is known.
 */
export function useIsAdvanceOnly(): boolean {
	const workspace = useSelector(
		({ workspace }: RootState) => workspace.workspaceInfo,
	);

	const isReady = !!workspace?._id;

	if (!isReady) {
		return false;
	}

	const isNicheInboxEnabled = !!workspace?.nicheInboxEnabled;

	const hasVideoMessagingPlan = checkWorkspaceHasVideoMessagingPlan(workspace);
	const hasSMSPlan = checkWorkspaceHasSMSPlan(workspace);
	const hasReelsPlan = checkWorkspaceHasReelsPlan(workspace);
	const hasAVideoSendingPlan = hasVideoMessagingPlan || hasReelsPlan;

	const isSMSSetupCompleted = checkSMSSetupCompleted(workspace);
	const isWhatsappSetupCompleted = checkWhatsAppSetupCompleted(workspace);
	const isEmailSetupCompleted = checkEmailSetupCompleted(workspace);

	const hasTextSendingSetup =
		(hasSMSPlan || hasAVideoSendingPlan) &&
		(isSMSSetupCompleted || isWhatsappSetupCompleted);

	const hasEmailSendingSetup = hasAVideoSendingPlan && isEmailSetupCompleted;

	const hasSendingSetup = hasTextSendingSetup || hasEmailSendingSetup;

	return isNicheInboxEnabled && !hasSendingSetup;
}

export default useIsAdvanceOnly;
